import { useEffect, useState } from "react";
import { Icon } from "@iconify-icon/react";
import { AnimatePresence, motion } from "motion/react";

import { cn } from "@/lib/utils";
import { Button } from "@/components/ui/button";
import { playNotificationSound } from "@/features/classroom/utils/notificationSound";

interface ToastProps {
  id: string | number;
  title?: string;
  description?: string;
  type?: "default" | "success" | "error" | "warning" | "info" | "loading" | "important";
  icon?: string;
  duration?: number;
  audio?: boolean;
  action?: {
    label: string;
    onClick: () => void;
  };
  cancel?: {
    label: string;
    onClick: () => void;
  };
  onDismiss?: () => void;
  visible?: boolean;
}

// Gradient color combinations for different toast types
const toastStyles = {
  default: {
    gradient: { from: "from-slate-400/40", to: "to-slate-500/60" },
    iconBg: "bg-slate-500/20",
    iconColor: "text-slate-200",
    icon: "lucide:bell",
    borderColor: "border-slate-500/20",
  },
  success: {
    gradient: { from: "from-green-400/40", to: "to-emerald-500/60" },
    iconBg: "bg-green-500/20",
    iconColor: "text-green-200",
    icon: "lucide:check-circle",
    borderColor: "border-green-500/20",
  },
  error: {
    gradient: { from: "from-red-400/40", to: "to-rose-500/60" },
    iconBg: "bg-red-500/20",
    iconColor: "text-red-200",
    icon: "lucide:x-circle",
    borderColor: "border-red-500/20",
  },
  warning: {
    gradient: { from: "from-amber-400/40", to: "to-orange-500/60" },
    iconBg: "bg-amber-500/20",
    iconColor: "text-amber-200",
    icon: "lucide:alert-triangle",
    borderColor: "border-amber-500/20",
  },
  info: {
    gradient: { from: "from-blue-400/40", to: "to-cyan-500/60" },
    iconBg: "bg-blue-500/20",
    iconColor: "text-blue-200",
    icon: "lucide:info",
    borderColor: "border-blue-500/20",
  },
  loading: {
    gradient: { from: "from-purple-400/40", to: "to-indigo-500/60" },
    iconBg: "bg-purple-500/20",
    iconColor: "text-purple-200",
    icon: "lucide:loader-2",
    borderColor: "border-purple-500/20",
  },
  important: {
    gradient: { from: "from-purple-400/40", to: "to-pink-500/60" },
    iconBg: "bg-purple-500/20",
    iconColor: "text-purple-200",
    icon: "lucide:award",
    borderColor: "border-purple-500/20",
  },
};

export const CustomToast = ({
  title,
  description,
  type = "default",
  icon,
  action,
  cancel,
  onDismiss,
  visible = true,
  audio = false,
}: ToastProps) => {
  const [isVisible, setIsVisible] = useState(visible);
  const [backgroundConfig] = useState(() => {
    // Create a unique background pattern for each toast
    const availablePositions = ["left-right", "right-left"];
    const selectedPosition =
      availablePositions[Math.floor(Math.random() * availablePositions.length)];

    return {
      positioning: selectedPosition,
      opacity: Math.random() * 0.05 + 0.05, // Random opacity between 0.02 and 0.05
    };
  });

  const style = toastStyles[type];
  const displayIcon = icon || style.icon;

  useEffect(() => {
    if (audio && isVisible) {
      playNotificationSound();
    }
  }, [audio, isVisible]);

  useEffect(() => {
    setIsVisible(visible);
  }, [visible]);

  const handleDismiss = () => {
    setIsVisible(false);
    setTimeout(() => {
      onDismiss?.();
    }, 200);
  };

  // Get circle positioning classes based on random configuration
  const getCirclePositions = () => {
    switch (backgroundConfig.positioning) {
      case "left-right":
        return {
          circle1: "absolute -top-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-br",
          circle2: "absolute -bottom-8 -right-8 h-16 w-16 rounded-full bg-gradient-to-tr",
        };
      case "right-left":
        return {
          circle1: "absolute -top-12 -right-12 h-24 w-24 rounded-full bg-gradient-to-bl",
          circle2: "absolute -bottom-8 -left-8 h-16 w-16 rounded-full bg-gradient-to-tl",
        };
      default:
        return {
          circle1: "absolute -top-12 -left-12 h-24 w-24 rounded-full bg-gradient-to-br",
          circle2: "absolute -bottom-8 -right-8 h-16 w-16 rounded-full bg-gradient-to-tr",
        };
    }
  };

  const circlePositions = getCirclePositions();

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          animate={{ opacity: 1, scale: 1, y: 0 }}
          className={cn(
            "relative z-[5003]! w-full max-w-md overflow-hidden rounded-2xl min-md:min-w-xs",
            "bg-foreground backdrop-blur-3xl",
            `border ${style.borderColor}`,
            "p-4",
          )}
          exit={{ opacity: 0, scale: 0.95, y: -20 }}
          initial={{ opacity: 0, scale: 0.95, y: -20 }}
          transition={{ duration: 0.3, ease: "easeOut" }}
        >
          {/* Background Pattern with Toast-Type Colors */}
          <div
            className="absolute inset-0 overflow-hidden"
            style={{ opacity: backgroundConfig.opacity }}
          >
            <div className={cn(circlePositions.circle1, style.gradient.from, style.gradient.to)} />
            <div className={cn(circlePositions.circle2, style.gradient.from, style.gradient.to)} />
          </div>

          {/* Content */}
          <div className="relative z-10">
            {/* Header */}
            <div className="flex items-center gap-3">
              {/* Icon */}
              <div
                className={cn(
                  "flex h-8 w-8 flex-shrink-0 items-center justify-center rounded-lg backdrop-blur-sm",
                  style.iconBg,
                )}
              >
                <Icon
                  className={cn(style.iconColor, type === "loading" && "animate-spin")}
                  height="1rem"
                  icon={displayIcon}
                  width="1rem"
                />
              </div>

              {/* Text Content */}
              <div className="h-full min-w-0 flex-1 items-center">
                {title && (
                  <h4 className="text-accent-foreground text-sm leading-tight font-semibold">
                    {title}
                  </h4>
                )}
                {description && (
                  <p
                    className={cn(
                      "text-accent-foreground/80 text-sm leading-relaxed",
                      title && "mt-1",
                    )}
                  >
                    {description}
                  </p>
                )}
              </div>

              {/* Close Button */}
              <Button
                className="bg-muted/20 hover:bg-muted/30 h-6 w-6 flex-shrink-0 rounded-lg"
                size="icon"
                variant="ghost"
                onClick={handleDismiss}
              >
                <Icon
                  className="text-accent-foreground"
                  height="0.75rem"
                  icon="lucide:x"
                  width="0.75rem"
                />
              </Button>
            </div>

            {/* Actions */}
            {(action || cancel) && (
              <div className="mt-3 flex items-center gap-2">
                {cancel && (
                  <Button
                    className="bg-muted/20 hover:bg-muted/30 border-border/50 h-8 rounded-lg px-3 text-xs"
                    size="sm"
                    variant="outline"
                    onClick={() => {
                      cancel.onClick();
                      handleDismiss();
                    }}
                  >
                    {cancel.label}
                  </Button>
                )}
                {action && (
                  <Button
                    className="bg-accent-foreground text-primary hover:bg-accent-foreground/80 h-8 rounded-lg px-3 text-xs"
                    size="sm"
                    onClick={() => {
                      action.onClick();
                      handleDismiss();
                    }}
                  >
                    {action.label}
                  </Button>
                )}
              </div>
            )}
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
